import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiRequest } from '../api/client';
import { callAction } from '../app/actions';
import { useAppState } from '../app/state';
import { canReslug } from '../lib/permissions';
import { inEditMode } from '../lib/mode';
import { slugifyBase } from '../lib/slug';

/**
 * "Change this address" — the one door out of a frozen slug (V0.45.1).
 *
 * ONE COMPONENT FOR BOTH NAME BARS. A node's title row and the doc bar's centre zone hold the
 * same three controls in the same order — star, bell, this — and the only difference between
 * the two cases is which endpoint answers. Two copies of a dialog that can break somebody
 * else's link is not a thing this codebase should own.
 *
 * ── WHY IT IS DRAWN THE WAY IT IS ────────────────────────────────────────────────────────
 *  - **Two gates, both required.** `RESLUG` (a standalone role — the full-editor macro does
 *    NOT imply it, see lib/permissions.ts) and the map's Edit toggle. Edit mode is not
 *    decoration: `doc-overlay-standard.png` photographs the whole doc bar in VIEW mode, so a
 *    button always drawn there would move a pixel baseline, and gating on Edit is the rule
 *    V0.44.1 already applies to the ownership box in that same bar.
 *  - **ABSENT, not disabled,** when either gate fails — nothing hints at a power this account
 *    does not have.
 *  - **The "!" is the whole discovery story.** An address that has stopped following its name
 *    is not something you go looking for, so the button says so. The predicate is the SERVER's
 *    (`slugDrifted` in server/src/lib/slug.ts): it compares against the label the slug was
 *    named by, English first, which this client is never sent. Guessing from the on-screen
 *    label would light the mark up on every node the moment somebody switched to French.
 *  - **The dialog opens on the address the name implies,** because that is the answer in the
 *    overwhelming majority of cases and typing it out again is busywork.
 *
 * The mark is only half of it: renaming closes the popup, so the bell carries the same fact
 * (the `slug_stale` notification) for the moment nobody is looking at this button.
 */

type Kind = 'node' | 'doc';

interface Facts {
  slug: string;
  sourceLabel: string;
  suggestion: string;
  drifted: boolean;
  acknowledgedFor: string | null;
  aliases: string[];
}

interface Outcome {
  slug: string;
  changed: boolean;
  drifted: boolean;
  aliasKept: string | null;
  released: string | null;
}

const PATH: Record<Kind, (id: string) => string> = {
  node: (id) => `/api/nodes/${encodeURIComponent(id)}/slug`,
  doc: (id) => `/api/docs/${encodeURIComponent(id)}/slug`,
};

/** What the address reads as in the URL bar, so the dialog shows a place and not a fragment. */
const PREFIX: Record<Kind, string> = { node: 'node', doc: 'doc' };

export function ReslugButton({
  kind,
  targetId,
  drifted,
  id,
}: {
  kind: Kind;
  targetId: string;
  /** From the server, via the node or `s.docSlugDrift` — see the header. */
  drifted: boolean;
  /** The element id the specs drive this by. */
  id: string;
}) {
  const s = useAppState();
  const [open, setOpen] = useState(false);

  // BOTH gates. Edit mode — the ONE flag since V0.53.5, which the doc bar's own switch flips —
  // is what keeps the view-mode doc-bar baseline still; `canReslug` is a bare role check,
  // deliberately not `brickOrMacro`.
  if (!canReslug(s.perm) || !inEditMode(s)) return null;

  return (
    <>
      <button
        type="button"
        className={drifted ? 'favbtn reslugbtn drift' : 'favbtn reslugbtn'}
        id={id}
        title={
          drifted
            ? // The stale address itself, where this side has it: a node carries its slug in
              // the map payload; a document's is not in the snapshot, so the dialog says it.
              `The web address no longer matches this name${
                kind === 'node' && s.nodes[targetId]?.slug
                  ? ` — /${s.currentLocale}/${PREFIX[kind]}/${s.nodes[targetId].slug}`
                  : ''
              }`
            : 'Change this web address'
        }
        aria-label={drifted ? 'The web address no longer matches this name' : 'Change this web address'}
        onClick={() => setOpen(true)}
      >
        <svg className="ico">
          <use href="#i-reslug" />
        </svg>
        {drifted ? (
          <span className="reslug-badge" aria-hidden="true">
            !
          </span>
        ) : null}
      </button>
      {open ? <ReslugDialog kind={kind} targetId={targetId} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/* ------------------------------------------------------------------------------------ *
 * The dialog. Its own component so the button costs nothing until it is pressed, and so   *
 * the FACTS are fetched at open rather than carried in the map payload — the suggestion   *
 * and the current aliases are needed by exactly one screen, at one moment.                *
 * ------------------------------------------------------------------------------------ */

function ReslugDialog({
  kind,
  targetId,
  onClose,
}: {
  kind: Kind;
  targetId: string;
  onClose: () => void;
}) {
  const s = useAppState();
  const [facts, setFacts] = useState<Facts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [release, setRelease] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    void apiRequest<Facts>(PATH[kind](targetId)).then(
      (f) => {
        if (!live) return;
        setFacts(f);
        // The address the name implies — the answer almost every time.
        setDraft(f.suggestion);
      },
      (e: Error) => live && setError(e.message),
    );
    return () => {
      live = false;
    };
  }, [kind, targetId]);

  useEffect(() => {
    if (facts) {
      input.current?.focus();
      input.current?.select();
    }
  }, [facts]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    // Capture, because the popup's own Escape handler would otherwise close the whole popup
    // out from under this dialog.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const send = async (body: { slug?: string; acknowledge?: boolean; release?: boolean }) => {
    setBusy(true);
    try {
      const out = await apiRequest<Outcome>(PATH[kind](targetId), { method: 'PUT', body });
      // The map carries the address and the mark, so both kinds refetch it rather than
      // patching two copies of the same fact into local state.
      await callAction('afterReslug', kind, targetId, out.slug);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change this address.');
      setBusy(false);
    }
  };

  // The live preview. `slugify` is the client's mirror of the server's `slugifyBase`, and says
  // so in its own header — it deliberately does not handle collisions, which is why the note
  // below promises only that the server re-resolves.
  const settled = slugifyBase(draft, '');
  const prefix = `/${s.currentLocale}/${PREFIX[kind]}/`;
  const what = kind === 'node' ? 'node' : 'document';

  /**
   * PORTALLED TO THE BODY (V0.49.2), and it has to be.
   *
   * The scrim is `position: fixed; inset: 0`, which means the viewport — unless an ancestor
   * has a `transform`, a `filter` or a `backdrop-filter`, any of which makes that ancestor the
   * containing block for fixed descendants instead. `.docbar` has `backdrop-filter: blur(10px)`,
   * so rendered in place the dialog was laid out inside a strip ~56px tall at the top of the
   * screen: it appeared behind the page rather than over it, and the scrim's own `overflow:auto`
   * turned the difference into a scrollbar. A node's popup has no such property, which is why
   * the same component looked right there and wrong here.
   *
   * A portal is the fix rather than hunting the property down, because the component is placed
   * by its callers — there are two bars today and nothing stops a third — and `position: fixed`
   * meaning the viewport is the assumption the whole dialog is drawn on.
   */
  return createPortal(
    <div className="reslug-scrim" id="reslug-scrim" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="reslug-dlg" role="dialog" aria-modal="true" aria-labelledby="reslug-title">
        <h4 id="reslug-title">
          <svg className="ico">
            <use href="#i-reslug" />
          </svg>
          Change this {what}’s web address
        </h4>

        {error ? (
          <div className="reslug-note warn" id="reslug-error">
            {error}
          </div>
        ) : null}

        {!facts ? (
          <div className="reslug-note" id="reslug-loading">
            Loading…
          </div>
        ) : (
          <>
            <div className="reslug-subject" id="reslug-subject">
              {facts.sourceLabel || '(unnamed)'}
            </div>

            <label className="reslug-lab">Address now</label>
            <div className="reslug-url was" id="reslug-now">
              <span className="pre">{prefix}</span>
              <span className="tail">{facts.slug}</span>
            </div>

            <label className="reslug-lab" htmlFor="reslug-input">
              New address
            </label>
            <input
              ref={input}
              className="fld"
              id="reslug-input"
              value={draft}
              spellCheck={false}
              disabled={busy}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="reslug-note" id="reslug-hint">
              {!settled
                ? 'An address needs at least one letter or digit.'
                : settled !== draft.trim()
                  ? `Tidied to ${settled}. Whatever you type is a suggestion; the server settles the final address.`
                  : 'The server settles the final address, adding -2 if something already holds this one.'}
            </div>

            <label className="reslug-release">
              <input
                type="checkbox"
                id="reslug-release"
                checked={release}
                disabled={busy}
                onChange={(e) => setRelease(e.target.checked)}
              />
              <span>
                <b>Also release the old address.</b> {prefix}
                {facts.slug} stops working, and the name goes back into the pool for something
                else to take.
              </span>
            </label>

            <div className={release ? 'reslug-conseq brk' : 'reslug-conseq keep'} id="reslug-conseq">
              {release
                ? `You are giving ${prefix}${facts.slug} up. Links people have already shared will stop working, and because it is no longer reserved something else may later take it.`
                : `Nothing breaks: ${prefix}${facts.slug} is remembered and stays reserved, so links people have already shared keep working.`}
            </div>

            {facts.aliases.length ? (
              <div className="reslug-note" id="reslug-aliases">
                Also still resolving here: {facts.aliases.join(', ')}
              </div>
            ) : null}

            <div className="reslug-foot">
              <button className="pbtn" id="reslug-cancel" disabled={busy} onClick={onClose}>
                Cancel
              </button>
              {/* Only an answer to a question the mark is asking. A settled address has
                  nothing to accept, so the button is not drawn. */}
              {facts.drifted ? (
                <button
                  className="pbtn"
                  id="reslug-keep"
                  disabled={busy}
                  title="Keep this address, and stop marking it"
                  onClick={() => void send({ acknowledge: true })}
                >
                  Keep the old address
                </button>
              ) : null}
              <button
                className="pbtn primary"
                id="reslug-save"
                disabled={busy || !settled}
                onClick={() => void send({ slug: settled, release })}
              >
                {busy ? 'Changing…' : 'Change the address'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
